// The arithmetic behind the Pastor Turnover summary. Pure functions over the
// tenure, posting and handover records, so the screen only lays the answers out.
//
// Business status lives in tenureStatus / postingStatus / handoverStatus; the
// plain `status` column is the lifecycle flag (ACTIVE / VOID) and is not read.

import { countBy, dateOf, isoOf, percent, yearsSince } from '../../utils/summaryStats';
import type { NamedCount } from '../../utils/summaryStats';
import { HANDOVER_SECTIONS } from './handoverChecklist';

export interface TenureLike {
  name?: string;
  designation?: string;
  startDate?: string;
  endDate?: string;
  /** Current / Transferred / Retired / Deceased. */
  tenureStatus?: string;
  prevChurch?: string;
  nextChurch?: string;
}

export interface PostingLike {
  name?: string;
  designation?: string;
  diocese?: string;
  orderDate?: string;
  effectiveDate?: string;
  /** Current / Archived. */
  postingStatus?: string;
}

export interface HandoverLike {
  recordId?: string;
  date?: string;
  outgoing?: string;
  incoming?: string;
  /** Pending / Completed. */
  handoverStatus?: string;
  // The checklist ticks (adminKeys, finCash…) are read by key from HANDOVER_SECTIONS.
}

export interface TenureRow {
  name: string;
  designation: string;
  startDate: string;
  endDate: string;
  status: string;
  prevChurch: string;
  nextChurch: string;
  /** Length in years to one decimal; an open tenure runs to today. */
  years: number;
}

export interface HandoverRow {
  key: string;
  date: string;
  outgoing: string;
  incoming: string;
  status: string;
  done: number;
  total: number;
  pct: number;
  missing: string[];
}

export interface ChecklistItemRow {
  key: string;
  label: string;
  section: string;
  /** Handovers that ticked this item. */
  done: number;
}

export interface PastorSummary {
  current: TenureRow | null;
  /** Completed years the current pastor has served. */
  currentYears: number | null;
  pastors: number;
  timeline: TenureRow[];
  /** Average length of tenures that have ended; null when none has. */
  avgTenure: number | null;
  longest: TenureRow | null;
  byStatus: NamedCount[];
  currentPostings: PostingLike[];
  postingsByYear: NamedCount[];
  handovers: HandoverRow[];
  checklistPct: number;
  items: ChecklistItemRow[];
  pending: HandoverRow[];
  /** Pending, or signed off with items still unticked — newest first. */
  watch: HandoverRow[];
}

const ITEMS = HANDOVER_SECTIONS.flatMap((s) => s.items.map((i) => ({ ...i, section: s.title })));

const DAY = 86_400_000;
const toTime = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).getTime();
};

/** A ticked checkbox; tolerates the string "true" from an imported row. */
export const isTicked = (v: unknown): boolean => v === true || v === 'true';

const tickOf = (h: HandoverLike, key: string): boolean =>
  isTicked((h as unknown as Record<string, unknown>)[key]);

/** A tenure's business status; a blank one is read from whether it has ended. */
export const tenureStatusOf = (t: TenureLike): string =>
  String(t.tenureStatus || '').trim() || (dateOf(t.endDate) ? 'Ended' : 'Current');

const yearsBetween = (start: string | null, end: string | null, now: Date): number => {
  if (!start) return 0;
  const to = end || isoOf(now);
  return Math.max(0, Math.round(((toTime(to) - toTime(start)) / DAY / 365.25) * 10) / 10);
};

export const pastorSummary = (
  tenures: TenureLike[], postings: PostingLike[], handovers: HandoverLike[], now: Date = new Date()
): PastorSummary => {
  const timeline: TenureRow[] = tenures
    .map((t) => {
      const start = dateOf(t.startDate);
      const end = dateOf(t.endDate);
      return {
        name: t.name || '-',
        designation: t.designation || '',
        startDate: start || '',
        endDate: end || '',
        status: tenureStatusOf(t),
        prevChurch: t.prevChurch || '',
        nextChurch: t.nextChurch || '',
        years: yearsBetween(start, end, now)
      };
    })
    .sort((a, b) => b.startDate.localeCompare(a.startDate));

  // The sitting pastor: a Current tenure, the latest to start if several are.
  const current = timeline.find((r) => r.status === 'Current') || null;
  const ended = timeline.filter((r) => r.endDate && r.startDate);
  const longest = timeline.reduce<TenureRow | null>((best, r) => (!best || r.years > best.years ? r : best), null);

  const rows: HandoverRow[] = handovers
    .map((h, i) => {
      const missing = ITEMS.filter((it) => !tickOf(h, it.key)).map((it) => it.label);
      const done = ITEMS.length - missing.length;
      return {
        key: h.recordId || `${h.date}|${i}`,
        date: dateOf(h.date) || '',
        outgoing: h.outgoing || '-',
        incoming: h.incoming || '-',
        status: String(h.handoverStatus || '').trim() || 'Pending',
        done,
        total: ITEMS.length,
        pct: percent(done, ITEMS.length),
        missing
      };
    })
    .sort((a, b) => b.date.localeCompare(a.date));

  const ticked = rows.reduce((n, r) => n + r.done, 0);

  return {
    current,
    currentYears: current ? yearsSince(current.startDate, now) : null,
    pastors: new Set(timeline.map((r) => r.name.trim().toLowerCase()).filter((n) => n && n !== '-')).size,
    timeline,
    avgTenure: ended.length
      ? Math.round((ended.reduce((n, r) => n + r.years, 0) / ended.length) * 10) / 10
      : null,
    longest,
    byStatus: countBy(timeline, (r) => r.status),
    currentPostings: postings.filter((p) => String(p.postingStatus || '').trim() === 'Current'),
    postingsByYear: countBy(postings, (p) => (dateOf(p.effectiveDate) || dateOf(p.orderDate) || '').slice(0, 4))
      .sort((a, b) => Number(a.name === 'Not set') - Number(b.name === 'Not set') || b.name.localeCompare(a.name)),
    handovers: rows,
    checklistPct: percent(ticked, rows.length * ITEMS.length),
    items: ITEMS.map((it) => ({
      key: it.key, label: it.label, section: it.section,
      done: handovers.filter((h) => tickOf(h, it.key)).length
    })),
    pending: rows.filter((r) => r.status !== 'Completed'),
    watch: rows.filter((r) => r.status !== 'Completed' || r.missing.length > 0)
  };
};
